// ============================================================
// 契約状態 API クライアント (契約満了アクセス制御・SPEC §2-3)
// FE はルート遷移ごとにこれを呼び、契約満了なら強制ログアウトする。
// ============================================================

import ipponApi from "../config/axios";

const BaseURL = import.meta.env.VITE_API_URL;

export interface ContractStatus {
  expired: boolean;
  contractStatus: string | null;
  contractStartDate: string | null;
  contractEndDate: string | null;
  terminatedAt: string | null;
  /** 管理画面化（SPEC §7.4）：契約前・契約終了の両方を含む利用可否 */
  allowed?: boolean;
  code?: ClientAccessCode;
  /** 解約後の猶予期間中（ログインは可・AI 機能は不可・SPEC §7.4.6） */
  aiDisabled?: boolean;
  /** 猶予期間中にログインできる最終日 YYYY-MM-DD */
  loginGraceEndDate?: string;
}

export type ClientAccessCode = "CONTRACT_NOT_STARTED" | "CONTRACT_EXPIRED";

/** 契約による強制ログアウトの理由をログイン画面に出す文言（SPEC §7.4.1） */
export const CLIENT_ACCESS_MESSAGES: Record<ClientAccessCode, string> = {
  CONTRACT_NOT_STARTED: "ご契約の開始日前のためログインできません。",
  CONTRACT_EXPIRED: "契約期間が終了しています。継続をご希望の場合はお問い合わせください。",
};

export const isClientAccessCode = (value: unknown): value is ClientAccessCode =>
  value === "CONTRACT_NOT_STARTED" || value === "CONTRACT_EXPIRED";

/** 契約の有効性を取得する。401(CONTRACT_EXPIRED) は axios interceptor が強制ログアウト。 */
export async function fetchContractStatus(): Promise<ContractStatus> {
  const res = await ipponApi.get(`${BaseURL}/api/v1/contract/status`);
  return res.data.data as ContractStatus;
}

/**
 * 契約状態から、強制ログアウトする理由を決める（遮断しないなら null）。
 * allowed を返す BE（管理画面化以降）はそれに従い、解約後の猶予期間（allowed=true）では
 * 旧来の expired が立っていても遮断しない。allowed を返さない古い BE だけ expired で判定する。
 */
export const resolveContractDenial = (status: ContractStatus): ClientAccessCode | null => {
  if (status.allowed !== undefined) return status.allowed ? null : status.code ?? "CONTRACT_EXPIRED";
  return status.expired ? "CONTRACT_EXPIRED" : null;
};

/** YYYY-MM-DD（ISO 日時でも可）を YYYY/MM/DD にする */
const toSlashDate = (value: string): string => value.slice(0, 10).replace(/-/g, "/");

/** 解約後の猶予期間の案内（SPEC §7.4.6）。猶予期間でなければ null */
export const buildGraceNotice = (status: ContractStatus): string | null => {
  if (!status.aiDisabled || !status.loginGraceEndDate || !status.contractEndDate) return null;
  return `ご契約は ${toSlashDate(status.contractEndDate)} で終了しました。${toSlashDate(
    status.loginGraceEndDate
  )} までデータの確認のためにログインできます（AI 機能はご利用いただけません）。`;
};
